import { useCallback, useMemo } from 'react';

import {
  adminRulesApi,
  useCreateAdminRuleMutation,
  useDeleteAdminRuleMutation,
  useGetAdminRuleQuery,
  useGetAdminRulesQuery,
  useSearchAdminRulesQuery,
  useUpdateAdminRuleMutation,
} from '../../store/adminApi';
import type { RuleScope, RuleType } from '../../types';

interface UseAdminRulesApiParams {
  /** Organization ID to filter rules for, null = global rules */
  organizationId: string | null;
  /** Current page number */
  page?: number;
  /** Items per page */
  perPage?: number;
  /** Scope filter */
  scope?: RuleScope;
  /** Rule type filter */
  ruleType?: RuleType | 'all';
  /** Search query (when provided, uses search endpoint instead of list) */
  searchQuery?: string;
}

/**
 * useAdminRulesApi Hook
 *
 * Wrapper hook that provides a unified interface for admin rules API operations.
 * Handles context switching between global and organization-scoped rules.
 */
export const useAdminRulesApi = ({
  organizationId,
  page = 1,
  perPage = 20,
  scope = 'both',
  ruleType = 'all',
  searchQuery = '',
}: UseAdminRulesApiParams) => {
  const isSearching = searchQuery.length > 0;

  // List query
  const listQuery = useGetAdminRulesQuery(
    {
      page,
      perPage,
      organizationId: organizationId ?? undefined,
      scope,
      ruleType: ruleType === 'all' ? undefined : ruleType,
    },
    { skip: isSearching },
  );

  // Search query
  const searchQueryResult = useSearchAdminRulesQuery(
    {
      q: searchQuery,
      topK: perPage,
      organizationId: organizationId ?? undefined,
      ruleType: ruleType === 'all' ? undefined : ruleType,
    },
    { skip: !isSearching },
  );

  // Mutations
  const [createRule, createMutation] = useCreateAdminRuleMutation();
  const [updateRule, updateMutation] = useUpdateAdminRuleMutation();
  const [deleteRule, deleteMutation] = useDeleteAdminRuleMutation();

  // Derived state
  const isLoading = isSearching
    ? searchQueryResult.isLoading || searchQueryResult.isFetching
    : listQuery.isLoading || listQuery.isFetching;

  const error = isSearching ? searchQueryResult.error : listQuery.error;

  // Get rules data - admin API now returns same structure as provider API
  const rules = useMemo(() => {
    if (isSearching) {
      // Search results - use the results array directly
      return searchQueryResult.data?.results || [];
    }
    // List results - items are already in ProviderRuleResponse format
    return listQuery.data?.items || [];
  }, [isSearching, searchQueryResult.data, listQuery.data]);

  const totalPages = useMemo(() => {
    if (isSearching) {
      return 1; // Search doesn't support pagination
    }
    return listQuery.data?.total_pages || 1;
  }, [isSearching, listQuery.data]);

  const total = useMemo(() => {
    if (isSearching) {
      return searchQueryResult.data?.total_results || 0;
    }
    return listQuery.data?.total || 0;
  }, [isSearching, searchQueryResult.data, listQuery.data]);

  // Refetch function
  const refetch = useCallback(() => {
    if (isSearching) {
      searchQueryResult.refetch();
    } else {
      listQuery.refetch();
    }
  }, [isSearching, searchQueryResult, listQuery]);

  // Invalidate cache for the current context
  const invalidateCache = useCallback(() => {
    const tagId = organizationId ?? 'GLOBAL';
    adminRulesApi.util.invalidateTags([
      { type: 'AdminRuleList', id: tagId },
      { type: 'AdminRuleSearch', id: tagId },
    ]);
  }, [organizationId]);

  return {
    // Data
    rules,
    total,
    totalPages,
    isLoading,
    error,
    isSearching,

    // Mutations
    createRule,
    updateRule,
    deleteRule,

    // Mutation states
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,

    // Actions
    refetch,
    invalidateCache,
  };
};

// Re-export the individual rule query hook for detail panel usage
export { useGetAdminRuleQuery };

